import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import { MIconButton } from 'components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/base.css';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { unwrapResult } from '@reduxjs/toolkit';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { deleteEmployeeApi, getAllSPEmployeeApi, getEmployeeResetPasswordApi } from 'redux/spEmployee/spEmployeeApi';
import DialogData from './DialogData';

const openProductImage = (data) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '35px', // Adjust as needed
      height: '35px', // Adjust as needed
      borderRadius: '50%',
      overflow: 'hidden',
      border: '1px solid #ccc',
      position: 'relative',
      left: '35%' // Optional: Adds a border similar to WhatsApp DP
    }}
  >
    <img
      src={data?.image || 'default-image-url'} // Replace 'default-image-url' with a fallback image URL
      alt={data?.alt || 'Image Alt Text'} // Replace 'Image Alt Text' with fallback alt text
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain' // Ensures the image covers the area without distortion
      }}
    />
  </div>
);

const filterValue = [
  { name: 'employeeId', operator: 'startsWith', type: 'string', value: '' },
  { name: 'employeeLoginId', operator: 'startsWith', type: 'string', value: '' },
  { name: 'name', operator: 'startsWith', type: 'string', value: '' },
  { name: 'designation', operator: 'startsWith', type: 'string', value: '' },
  { name: 'email', operator: 'startsWith', type: 'string', value: '' },
  { name: 'primaryPhoneNumber', operator: 'startsWith', type: 'string', value: '' },
  { name: 'secondaryPhoneNumber', operator: 'startsWith', type: 'string', value: '' },
  { name: 'address', operator: 'startsWith', type: 'string', value: '' },
  { name: 'dateOfBirth', operator: 'startsWith', type: 'string', value: '' }
];

const gridStyle = { height: '100%', contain: 'none' };

const actionCellRenderer = (data, user) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'center' }}>
    {user?.accessList?.USER?.UPDATE && (
      <Tooltip title="Edit Employee">
        <IconButton onClick={() => data?.editEmployee(data)}>
          <EditIcon fontSize="small" sx={{ color: '#05acc1' }} />
        </IconButton>
      </Tooltip>
    )}
    {user?.accessList?.USER?.UPDATE && (
      <Tooltip title="Reset Password">
        <IconButton onClick={() => data?.resetPassword(data)}>
          <LockResetIcon fontSize="small" sx={{ color: 'green' }} />
        </IconButton>
      </Tooltip>
    )}
    {user?.accessList?.USER?.DELETE && (
      <Tooltip title="Delete employee">
        <IconButton onClick={() => data?.deleteApi(data)}>
          <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const openEmployeeDetails = (data) => (
  <Button variant="text" onClick={() => data?.showEmployeeDetails(data)}>
    {data?.employeeId}
  </Button>
);

const createRowData = (data, callBackFns) => {
  const employee = {
    id: data?._id,
    image: data?.profileImageUrl,
    employeeId: data?.employeeId,
    name: data?.name,
    designation: data?.designation,
    email: data?.email,
    primaryPhoneNumber: data?.phoneNumber?.primary ? `+91${data?.phoneNumber?.primary}` : '',
    userName: data?.userName,
    employeeLoginId: data?.employeeAuthDetails[0]?.userName,
    lastLoginDateTime: data?.loginDate ? moment(data?.loginDate).format('YYYY-MM-DD HH:mm:ss') : '',
    action: '',
    ...callBackFns
  };

  return employee;
};

export default function EmployeeTable(props) {
  const { spEmployeeList = [], selectedTab, isSPEmployeeLoading, user } = props;
  const [gridRef, setGridRef] = useState(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState([]);
  const [openEmployeeDetailDialog, setEmployeeDetailDialog] = useState(false);
  const [employeeData, setEmployeeData] = useState({});
  const [employeeLoginId, setEmployeeLoginId] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedRecord, selectRecord] = useState({});

  const columns = [
    {
      name: 'profileImage',
      header: 'Profile Image',
      minWidth: 200,
      render: ({ data }) => openProductImage(data)
    },
    {
      name: 'employeeId',
      header: 'Employee Id',
      minWidth: 100,
      render: ({ data }) => openEmployeeDetails(data)
    },
    {
      name: 'employeeLoginId',
      header: 'Employee Login UserName',
      minWidth: 100
    },
    {
      name: 'lastLoginDateTime',
      header: 'Employee Last Login',
      minWidth: 200
    },
    {
      name: 'name',
      header: 'Name',
      type: 'number',
      minWidth: 200
    },
    {
      name: 'designation',
      header: 'Designation',
      minWidth: 200
    },
    {
      name: 'email',
      header: 'Email',
      sortable: false,
      minWidth: 200
    },
    {
      name: 'primaryPhoneNumber',
      header: 'Primary PhoneNumber',
      sortable: false,
      minWidth: 160
    },
    {
      name: 'action',
      header: 'Action',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      minWidth: 160,
      render: ({ data }) => actionCellRenderer(data, user)
    }
  ];

  useEffect(() => {
    if (!_.isEmpty(spEmployeeList)) {
      const rowD = _.map(spEmployeeList, (data) =>
        createRowData(data, { showEmployeeDetails, editEmployee, resetPassword, deleteApi })
      );
      setRowData(rowD || []);
    } else {
      setRowData([]);
    }
  }, [selectedTab, spEmployeeList]);

  const showNotification = (variant, message) => {
    enqueueSnackbar(message, {
      variant,
      action: (key) => (
        <MIconButton size="small" onClick={() => closeSnackbar(key)}>
          <Icon icon={closeFill} />
        </MIconButton>
      )
    });
  };

  const editEmployee = (data) => {
    navigate(`/spEmployee/edit?employeeId=${data?.employeeId}`);
  };

  const resetPassword = (data) => {
    const params = {
      userName: data?.userName,
      employeeId: data?.employeeId
    };
    console.log(params, 'efre');
    dispatch(getEmployeeResetPasswordApi(params));
    showNotification('success', 'New password has sent to employee');
  };

  const deleteApi = (data) => {
    setDeleteDialog(true);
    selectRecord(data);
  };

  const onDeleteAgree = async () => {
    try {
      const params = {
        employeeId: selectedRecord?.employeeId,
        userName: user?.userName
      };
      const res = await dispatch(deleteEmployeeApi(params));
      showNotification('success', 'Employee Deleted Succesfully');
      unwrapResult(res);
      dispatch(getAllSPEmployeeApi({ userName: user?.userName }));
      closeDeleteDialog();
    } catch (err) {
      showNotification('error', 'Operation Failed');
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog(false);
    selectRecord({});
  };

  const showEmployeeDetails = (data) => {
    const employeeDetails = _.find(spEmployeeList, (d) => d?.employeeId === data?.employeeId);
    setEmployeeLoginId(data?.employeeLoginId);
    setEmployeeData(employeeDetails);
    setEmployeeDetailDialog(true);
  };

  return (
    <Box sx={{ height: '65vh', width: '100%' }}>
      <Dialog fullScreen open={openEmployeeDetailDialog} onClose={() => setEmployeeDetailDialog(false)}>
        <DialogData
          setEmployeeDetailDialog={setEmployeeDetailDialog}
          employeeData={employeeData}
          employeeLoginId={employeeLoginId}
        />
      </Dialog>

      <ConfirmationDialog
        open={deleteDialog}
        handleClose={closeDeleteDialog}
        onAgree={onDeleteAgree}
        dialogContent="Are you sure you want to delete this? You won't be able to recover the data after this action."
        titleContent="Delete Confirmation"
        agreeText="Delete"
        disagreeText="Cancel"
      />

      <ReactDataGrid
        onReady={setGridRef}
        idProperty="id"
        columns={columns}
        dataSource={rowData}
        style={gridStyle}
        defaultFilterValue={filterValue}
        loading={isSPEmployeeLoading}
      />
    </Box>
  );
}
